// packages/frontend/src/i18n/direction.ts

/**
 * Text direction plumbing for the app shell.
 *
 * The single source of truth for "is the UI mirrored?" is `document.documentElement.dir`.
 * Everything else — Tailwind's `rtl:` variant, the logical `ms-`/`ps-`/`start-` utilities,
 * and the `[dir="rtl"]` rules in globals.css — reads that attribute, so setting it here is
 * the only thing that has to happen when the language changes.
 *
 * Note this is app-chrome direction only. Generated book content has its own, separate
 * detection path in `src/utils/rtlDetection.ts` and is deliberately untouched.
 */

import { useTranslation } from 'react-i18next';

export type Direction = 'ltr' | 'rtl';

/**
 * Languages whose script runs right-to-left.
 *
 * Kept as a list rather than a single `'he'` check so adding Arabic later is a one-line
 * change here plus a locale directory — no plumbing work.
 */
export const RTL_LANGUAGES: readonly string[] = ['he', 'ar'];

/**
 * Strip any region/script subtag: `he-IL` and `he_IL` both resolve to `he`.
 *
 * i18next hands us whatever the browser reported (`navigator.language` is commonly
 * region-qualified), so a bare equality check against `'he'` would miss real users.
 */
function baseLanguage(code: string | null | undefined): string {
  if (!code) return '';
  return code.split(/[-_]/)[0].toLowerCase();
}

export function isRTLLanguage(code: string | null | undefined): boolean {
  return RTL_LANGUAGES.includes(baseLanguage(code));
}

export function directionForLanguage(code: string | null | undefined): Direction {
  return isRTLLanguage(code) ? 'rtl' : 'ltr';
}

/**
 * Write `dir` and `lang` onto <html> for the given language code.
 *
 * `lang` matters beyond direction: it drives font fallback selection, hyphenation, and
 * how screen readers pronounce the page. Returns the direction that was applied so
 * callers (and tests) can assert on it without re-reading the DOM.
 */
export function applyDocumentDirection(code: string | null | undefined): Direction {
  const direction = directionForLanguage(code);
  const root = document.documentElement;

  root.setAttribute('dir', direction);
  if (code) {
    root.setAttribute('lang', code);
  }

  return direction;
}

/**
 * Whether the UI is currently mirrored, for the handful of places that need the answer in
 * JavaScript rather than CSS — framer-motion's `x` is physical and has no logical form.
 *
 * Reads `resolvedLanguage` for the same reason `applyDocumentDirection` is called with it:
 * a browser asking for `he-IL` with no Hebrew bundle renders English and is *not* mirrored,
 * so a component keying off the requested code would disagree with the document's own `dir`
 * and animate from the wrong edge. `useTranslation` re-renders on `languageChanged`, so this
 * stays in step with the attribute.
 */
export function useIsRTL(): boolean {
  const { i18n } = useTranslation();
  return isRTLLanguage(i18n?.resolvedLanguage ?? i18n?.language);
}
